import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | already | error
  const [message, setMessage] = useState("");
  const ran = useRef(false); // guard StrictMode double-mount

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      setMessage("Missing token. Please use the link from your email.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((r) => {
        setStatus(r.data.status === "already_verified" ? "already" : "success");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "We couldn't verify this link. Try requesting a new one.");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-card border border-border rounded-lg p-10">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">← Railway Careers UK</Link>
        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 mx-auto mt-6 animate-spin" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Confirming your email…</h1>
          </>
        )}
        {(status === "success" || status === "already") && (
          <>
            <CheckCircle2 className="w-12 h-12 text-lime-600 mx-auto mt-6" data-testid="verify-success" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">
              {status === "already" ? "Already confirmed" : "Email confirmed"}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Thanks. You're now waiting on admin approval before you can post vacancies.
              We'll email you the moment your account is live — usually within one working day.
            </p>
            <Link to="/employer-portal/login" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors">
              Sign in to dashboard
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mt-6" data-testid="verify-error" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Couldn't confirm</h1>
            <p className="mt-3 text-sm text-muted-foreground">{message}</p>
            <Link to="/employer-portal/login" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors">
              Sign in & request new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
